import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatDateMain,
  getPlansForBusiness,
  isContactSalesPlan,
  isRecommendedPlan,
  planUnit,
  usageOrderTiers,
  type SubscriptionAudience,
  type SubscriptionInterval,
  type SubscriptionPreview,
} from '@hero/core';
import type { BillingUnit, Business, Currency } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { IntervalToggle } from '../IntervalToggle/IntervalToggle';
import { PlanCard, type PlanCardTier } from '../PlanCard/PlanCard';
import { CommitmentStepper } from '../CommitmentStepper/CommitmentStepper';
import {
  BillingOrderSummary,
  type OrderSummaryVariant,
} from '../BillingOrderSummary/BillingOrderSummary';
import { BillingRedirectOverlay } from '../BillingRedirectOverlay/BillingRedirectOverlay';

export interface BillingCheckoutScreenProps {
  /** Page heading. */
  title?: string;
  /** Sub-heading copy. */
  description?: string;

  // Plan picker state (controlled)
  planCode: string | undefined;
  onPlanCodeChange: (code: string) => void;
  interval: SubscriptionInterval;
  onIntervalChange: (interval: SubscriptionInterval) => void;
  committedVehicleQty: number;
  onCommittedVehicleQtyChange: (qty: number) => void;
  /**
   * Committed monthly order volume, for plans metered `order`. Held
   * separately from the seat count so switching plans doesn't reinterpret
   * one number as the other.
   */
  committedOrderQty: number;
  onCommittedOrderQtyChange: (qty: number) => void;
  /**
   * The plan the purchase will actually land on.
   *
   * Differs from `planCode` only on the credit ladder, where the tier
   * is earned by the volume rather than clicked. Pass the manager's
   * `resolvedPlanCode`; without it the grid would keep highlighting a
   * card the quantity no longer buys.
   */
  resolvedPlanCode?: string;
  currency: Currency;
  /**
   * Which brand's ladder to show. Defaults to `'hero'` — the
   * dashboard is the Hero surface. Present so a white-label checkout
   * page can reuse this screen without a fork.
   */
  business?: Business;
  /**
   * Which price list to quote from — a company or an owner-operator.
   *
   * Pass the manager's `audience`. It has to reach the cards because
   * each one prices itself at the volume that earns it, so a card left
   * on the default would print a company rate beside a freelancer's
   * total and read as the total being wrong.
   */
  audience?: SubscriptionAudience;
  /**
   * Which unit tab opens when nothing is selected yet.
   *
   * Defaulted by the caller from what the org actually does — an org
   * that has been creating orders and owns no trucks should not have to
   * find the second tab to see a price that fits it. Falls back to
   * `vehicle`, the historical default.
   */
  defaultUnit?: BillingUnit;

  // Summary
  /** Canonical preview from `computeSubscriptionPreview()`. */
  preview: SubscriptionPreview | null;
  variant: OrderSummaryVariant;
  /**
   * When the subscription will next renew. Formatted internally via
   * the shared `formatDateMain` util so the same timezone + locale
   * rules apply everywhere. Pass `subscription.currentPeriodEnd` for
   * change-plan; let the manager estimate it for subscribe.
   */
  nextRenewalAt?: Date;

  // CTA + flows
  onPrimaryClick: () => void;
  onContactSales?: () => void;
  isSubmitting?: boolean;

  /**
   * When true, the full-screen redirect overlay is mounted. Owned by
   * the parent so the manager can keep it up across the in-flight
   * request and the subsequent `window.location.assign` to Moyasar.
   */
  redirectOverlayOpen: boolean;
}

/**
 * Per-plan visual tier. Plan title and one-line description live in
 * `m-billing.json` under `plan.<code>` so they translate; the badge and
 * whether a plan appears at all come from `SUBSCRIPTION_CONFIG`. This
 * map is *only* the palette hint, so the row reads as an entry → brand
 * → premium progression.
 */
const PLAN_TIER: Record<string, PlanCardTier> = {
  free: 'soft',
  starter: 'soft',
  growth: 'brand',
  start: 'soft',
  pro: 'brand',
  max: 'premium',
  wathiqa_start: 'soft',
  wathiqa_pro: 'brand',
  wathiqa_max: 'premium',
  custom: 'premium',
};

/** Unit tabs, in the order they're offered. */
const UNIT_TABS: BillingUnit[] = ['vehicle', 'order'];

/**
 * Single-page billing checkout: plan picker stack on the left,
 * sticky order summary on the right. Owns the layout, plan-card grid,
 * and the in-flight redirect overlay; all state lives in the parent.
 *
 * Mode is driven by `variant`:
 * - `pay`    — paid scenario (new signup, trial conversion, mid-period
 *              upgrade). The primary CTA hands off directly to
 *              `useSubscriptionPayManager.pay` (no interstitial confirm
 *              dialog). The backend then either silently charges the
 *              saved Moyasar token or returns a hosted-checkout URL,
 *              which the parent navigates to.
 * - `free`   — free-plan shortcut (no redirect, no charge).
 * - `custom` — contact sales (no totals).
 */
export function BillingCheckoutScreen({
  title,
  description,
  planCode,
  onPlanCodeChange,
  interval,
  onIntervalChange,
  committedVehicleQty,
  onCommittedVehicleQtyChange,
  committedOrderQty,
  onCommittedOrderQtyChange,
  resolvedPlanCode,
  currency,
  business = 'hero',
  audience = 'org',
  defaultUnit = 'vehicle',
  preview,
  variant,
  nextRenewalAt,
  onPrimaryClick,
  onContactSales,
  isSubmitting,
  redirectOverlayOpen,
}: BillingCheckoutScreenProps) {
  const { t } = useTranslation('m-billing');
  const planCodes = getPlansForBusiness(business, { includeContactSales: true });
  // The interval badges describe one plan's discount curve, so they
  // follow the selection. With nothing selected they describe the plan
  // the opening tab leads with, which is what the user is looking at.
  const visiblePaidPlan =
    planCode && planCodes.includes(planCode) && !isContactSalesPlan(planCode)
      ? planCode
      : planCodes.find(
          code => !isContactSalesPlan(code) && planUnit(code) === defaultUnit,
        ) ?? planCodes[0];

  // The catalog sells two ways of paying, and a single row mixing "SAR 70
  // per truck" with "SAR 5 per order" cards is unreadable — the numbers
  // aren't comparable. So the grid shows one unit at a time.
  //
  // Which unit is showing is *derived* from the selected plan rather than
  // held as state: picking a tab selects that unit's first plan, which is
  // also the selection the summary and stepper need anyway. One source of
  // truth, and no way for the tab and the priced plan to disagree.
  //
  // `custom` is exempt from the split: it is a "contact sales" card whose
  // price is negotiated per Org, so it belongs under both tabs.
  const selectedUnit = planCode ? planUnit(planCode) : defaultUnit;
  const isUnitAgnostic = isContactSalesPlan;
  // On the credit ladder a plan is not a thing you pick, it is a thing
  // your volume reaches. So the cards stop being radio buttons and
  // become the ladder itself: the highlighted one is whatever the
  // current quantity earns, and clicking a card is a shortcut to the
  // quantity that earns it. Selling it any other way would let someone
  // click `max` at ten credits and watch the choice silently revert.
  const isCreditLadder = selectedUnit === 'order';
  const creditTierMinQty = (code: string) =>
    usageOrderTiers(business, audience).find(tier => tier.planCode === code)?.minQty;
  const highlightedPlanCode = isCreditLadder ? (resolvedPlanCode ?? planCode) : planCode;
  const plansByUnit = UNIT_TABS.map(unit => ({
    unit,
    codes: planCodes.filter(
      code => isUnitAgnostic(code) || planUnit(code) === unit,
    ),
  })).filter(group => group.codes.some(code => !isUnitAgnostic(code)));
  const visibleCodes =
    plansByUnit.find(group => group.unit === selectedUnit)?.codes ?? planCodes;

  const resolvedTitle = title ?? t('checkout.landing.title');
  const resolvedDescription = description ?? t('checkout.landing.description');

  const renewalLabel = nextRenewalAt
    ? formatDateMain({ date: nextRenewalAt, format: 'date' })
    : undefined;
  const planTitle = highlightedPlanCode
    ? t(`plan.${highlightedPlanCode}.title`, { defaultValue: highlightedPlanCode })
    : undefined;

  return (
    // `pb-12` keeps the last card off the viewport edge so the
    // CommitmentStepper / add-on slot has breathing room above the browser
    // chrome (especially on shorter screens where the right summary
    // pushes the page tall and the page itself doesn't scroll-pad).
    <div className="flex flex-col gap-6 pb-12">
      <header className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            {resolvedTitle}
          </h1>
          <p className="max-w-2xl text-sm text-gray-500">{resolvedDescription}</p>
        </div>
        {/* Credits have no term, so there is no commitment length to
            trade against a discount — the volume ladder plays that
            role. Leaving the toggle up would offer a choice that
            changes nothing on the total. */}
        {!isCreditLadder && (
          <IntervalToggle
            value={interval}
            onChange={onIntervalChange}
            savingsPlanCode={visiblePaidPlan}
            currency={currency}
          />
        )}
      </header>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        {/* Left column — unit tabs + plan cards + commitment stepper */}
        <div className="flex flex-col gap-5 lg:col-span-8">
          {plansByUnit.length > 1 && (
            <div
              role="tablist"
              className="inline-flex self-start rounded-full border border-gray-200 bg-white p-1 shadow-sm">
              {plansByUnit.map(group => {
                const active = group.unit === selectedUnit;
                const firstPlan = group.codes.find(code => !isUnitAgnostic(code));
                return (
                  <button
                    key={group.unit}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => firstPlan && onPlanCodeChange(firstPlan)}
                    className={cn(
                      'rounded-full px-4 py-1.5 text-sm font-medium transition',
                      active
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900',
                    )}>
                    {t(`basis_tab.${group.unit}`, {
                      defaultValue:
                        group.unit === 'vehicle' ? 'Pay per truck' : 'Pay per order',
                    })}
                  </button>
                );
              })}
            </div>
          )}

          <div className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-3')}>
            {visibleCodes.map(code => {
              return (
                <PlanCard
                  key={code}
                  planCode={code}
                  selected={highlightedPlanCode === code}
                  onSelect={() => {
                    const tierQty = isCreditLadder ? creditTierMinQty(code) : undefined;
                    if (tierQty !== undefined) onCommittedOrderQtyChange(tierQty);
                    else onPlanCodeChange(code);
                  }}
                  interval={interval}
                  currency={currency}
                  committedVehicleQty={committedVehicleQty}
                  committedOrderQty={committedOrderQty}
                  business={business}
                  audience={audience}
                  title={t(`plan.${code}.title`, { defaultValue: code })}
                  description={t(`plan.${code}.description`, { defaultValue: '' })}
                  badge={
                    code === 'free'
                      ? 'free_forever'
                      : isRecommendedPlan(code)
                        ? 'popular'
                        : null
                  }
                  tier={PLAN_TIER[code] ?? 'soft'}
                  isCustom={isContactSalesPlan(code)}
                  onContactSales={onContactSales}
                />
              );
            })}
          </div>

          {/*
            One stepper, bound to whichever quantity the selected plan
            meters. Showing both at once would ask the user to size a
            commitment they aren't buying.
          */}
          {selectedUnit === 'order' ? (
            <CommitmentStepper
              unit="order"
              value={committedOrderQty}
              onChange={onCommittedOrderQtyChange}
            />
          ) : (
            <CommitmentStepper
              unit="vehicle"
              value={committedVehicleQty}
              onChange={onCommittedVehicleQtyChange}
            />
          )}

          {/* Phase 2 slot: add-ons row mounts here. */}
        </div>

        {/* Right column — sticky summary, aligned to top of plan cards */}
        <div className="lg:col-span-4">
          <BillingOrderSummary
            preview={preview}
            planTitle={planTitle}
            nextRenewalLabel={renewalLabel}
            variant={variant}
            onPrimaryClick={onPrimaryClick}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      <BillingRedirectOverlay open={redirectOverlayOpen} />
    </div>
  );
}
